/**
 * All simulation tuning, in one file.
 *
 * The PHYSICS block is v1's, from `src/client/config.js:36-50`, with one
 * deliberate departure (GROUND_DECEL — see its comment). It is the most
 * valuable non-visual thing v1 produced: months of feel work that makes
 * movement read as Fall Guys rather than as a physics demo. The rest moved
 * from the client to here unchanged — the numbers did not get "cleaned up",
 * because the reason they feel good is that they were tuned by hand against a
 * running game and not derived from anything.
 */
import { TICK_HZ } from "@chaos/protocol";
/** Seconds per fixed-timestep tick. The only `delta` the sim ever sees. */
export const DT = 1 / TICK_HZ;
// ---------------------------------------------------------------------------
// Movement — v1 tuning; change a number only with a measured reason, like
// GROUND_DECEL's
// ---------------------------------------------------------------------------
export const PHYSICS = {
    GRAVITY: -76.5,
    /** Extra gravity while falling — the reason a jump arc feels snappy, not floaty. */
    FALL_MULTIPLIER: 2.2,
    /** Extra gravity on a released jump. Together these make jump height analog. */
    LOW_JUMP_MULTIPLIER: 4.0,
    TERMINAL_VELOCITY: -60,
    JUMP_FORCE: 26.5,
    /** Seconds after leaving ground during which a jump still counts. */
    COYOTE_TIME: 0.1,
    /** Seconds before landing during which a jump press is remembered. */
    JUMP_BUFFER_TIME: 0.1,
    /**
     * HALVED from v1's 16/26, and the arena is the reason.
     *
     * v1 was a party platformer on a 90-unit deck. This is a brawler on a 50-unit
     * one, and the speeds came across unchanged — so a sprint crossed the whole
     * arena in 1.9 seconds, and Volt, at 1.25x, did it in 1.5. There is no room
     * to read a telegraph, take an angle, or back out of a fight inside 1.5
     * seconds. Playtested verdict: "everything is a bit too fast, it's hard to
     * play this."
     *
     * Measured against the reference rather than nudged. Brawl Stars runs about 3
     * body-widths per second on a map roughly 30 body-widths across — call it ten
     * seconds to cross. A body here is 1 unit and the arena is 50, so at 26 we
     * were doing 26 body-widths per second: not twice their pace, closer to nine
     * times it.
     *
     * Halving overshot. Crossing went to ~3.8s and played as sluggish — this is a
     * 4-minute match with a closing zone, not a 2-minute objective map, and it
     * wants noticeably more urgency than the reference. Settled at 0.73x of v1:
     *
     *     26  -> 1.9s to cross   too fast to read anything
     *     13  -> 3.8s            too slow to feel like a brawler
     *     19  -> 2.6s            here
     *
     * Still roughly seven times the reference in body-widths per second, which is
     * a deliberate choice about this game rather than a failure to hit a target.
     *
     * ACCELERATION SCALES WITH IT, and that is what keeps the feel constant
     * through every one of these passes. Time to reach top speed is v/accel and
     * time to stop is v/decel, so scaling both by the same factor holds both
     * times where they were tuned and moves only the DISTANCES — which is the
     * only thing "too fast for this arena" was ever about.
     *
     *     top speed: 19/58 = 0.33s   (was 26/80 = 0.33s)
     *     stop:      19/175 = 0.109s (was 26/240 = 0.108s)
     */
    WALK_SPEED: 12,
    SPRINT_SPEED: 19,
    GROUND_ACCEL: 58,
    /**
     * NOT v1's number. v1 shipped 60, and 60 was tuned for a party platformer
     * with a flat 26 top speed, where a long comedy skid was part of the joke.
     * v2 multiplies speed by the roster's speedMult — Volt sprints at 32.5,
     * faster than v1 ever moved — and asks you to stop inside a 2.6m melee cone:
     * at 60, releasing the keys at Volt's sprint measured 10.4m of skating over
     * 600ms. At 240 the same release stops in 1.67m over 167ms (5 ticks), an
     * unmodified 26 sprint in 1.0m over 133ms, and Monolith's 22.1 in 0.67m over
     * 100ms — inside two body widths, still spread over enough ticks to read as
     * weight rather than a snap.
     *
     * Now 175, scaled by the same 0.73 as the speeds above. Stopping TIME is
     * v/decel, so scaling both together holds it at the 0.108s it was tuned to;
     * the stopping DISTANCE moves with everything else, which is the point.
     */
    GROUND_DECEL: 175,
    AIR_ACCEL: 22,
    AIR_DECEL: 7,
};
/**
 * The input magnitude that maps to exactly WALK_SPEED.
 *
 * The keyboard sends this for unmodified WASD and 1.0 with Shift held, so the
 * two discrete keyboard speeds are v1's 16 and 26 exactly. A stick feathered
 * below it creeps proportionally toward zero — the analog gait a discrete
 * sprint key never had. See `integrateMovement` for the ramp itself.
 */
export const WALK_INPUT = 0.5;
/** Ice multiplies both accel and decel down — you keep your momentum. v1 EntityBehaviors.js:76-77. */
export const ICE_ACCEL_MULTIPLIER = 0.15;
export const ICE_DECEL_MULTIPLIER = 0.08;
/** v1 default when a bounce trigger did not specify one (EntityBehaviors.js:39). */
export const DEFAULT_BOUNCE_FORCE = 18;
/** v1 default conveyor speed (EntityBehaviors.js:69). */
export const DEFAULT_CONVEYOR_SPEED = 6;
export const COYOTE_TICKS = Math.round(PHYSICS.COYOTE_TIME * TICK_HZ);
export const JUMP_BUFFER_TICKS = Math.round(PHYSICS.JUMP_BUFFER_TIME * TICK_HZ);
/**
 * How long a cast pressed during wind-up or recovery is held for.
 *
 * The same 0.1s the jump buffer uses. Long enough that pressing a fraction early
 * lands instead of vanishing; short enough that a press cannot fire so late it
 * surprises you.
 */
export const CAST_BUFFER_TICKS = JUMP_BUFFER_TICKS;
// ---------------------------------------------------------------------------
// Collider
// ---------------------------------------------------------------------------
/**
 * A vertical capsule matching v1's player box at the equator: 1 unit wide,
 * 2 units tall. `y` is the centre, so the feet are at `y - PLAYER_HALF_HEIGHT`.
 */
export const PLAYER_RADIUS = 0.5;
export const PLAYER_HALF_HEIGHT = 1.0;
/** How far below a platform top the feet may be and still count as landing. v1 used 0.5. */
export const GROUND_SNAP = 0.5;
/** Sentinel platform indices for "in the air" and "on the map's floor plane". */
export const GROUND_NONE = -1;
export const GROUND_FLOOR_PLANE = -2;
/** How hard two players push each other apart per tick. 1 = fully separated in one tick. */
export const PLAYER_SEPARATION_STRENGTH = 0.5;
// ---------------------------------------------------------------------------
// Plausibility bounds
// ---------------------------------------------------------------------------
/**
 * v1's `SPATIAL_CONSTANTS` (shared/constants.js:87-94) were hints handed to the
 * AI so it would not build unreachable platforms. Here they are enforcement.
 *
 * Read them for what they are: JUMP_MAX_HEIGHT / JUMP_MAX_HORIZONTAL describe
 * the largest gap a jump may be expected to clear — a map-authoring rule, used
 * by `isReachable`. They are NOT per-tick motion limits; a sprinting player
 * covers 4 units of ground in well under a second. The per-tick caps below are
 * derived from PHYSICS instead, which is the only honest source for them.
 */
export const SPATIAL = {
    JUMP_MAX_HEIGHT: 7.5,
    JUMP_MAX_HORIZONTAL: 4,
    JUMP_MAX_VERTICAL: 3,
};
/**
 * Hard cap on how far integration may move a player in one tick.
 *
 * Ceiling from the tuning: SPRINT 26 x fastest character 1.25 x Overload 1.40
 * = 45.5 u/s = 1.52 units/tick. Doubled to 3.0 so conveyors, wind, knockback
 * and a dash can all stack without the clamp ever firing in legitimate play —
 * this is a backstop against a sim bug or a NaN, not a gameplay rule. If it
 * fires, something is wrong upstream and the match is still bounded.
 */
export const MAX_STEP_XZ = 3.0;
/** Terminal velocity is 60 u/s = 2.0 units/tick. Same reasoning. */
export const MAX_STEP_Y = 3.0;
/** Displacement from abilities (blink, perch, displace) is bounded by the ability, not this. */
export const MAX_ABILITY_TELEPORT = 20;
// ---------------------------------------------------------------------------
// Travel — abilities that carry a body somewhere, over ticks
// ---------------------------------------------------------------------------
/**
 * The bound a travel's per-tick step is held under, in units.
 *
 * Equal to the integration backstops (MAX_STEP_XZ / MAX_STEP_Y), and still
 * never what shapes a move: `travelTicks` sizes a travel so that 2D/N fits
 * here, and the ease-out's real first step is D(2N-1)/N², strictly less. So
 * the clamp never fires on a travel and a 20 m Robotaxi ride grows from 9 to
 * 14 ticks rather than clipping its first step and arriving short. The
 * roster's own durations — a 3 m roll in 3 ticks, a 9 m dash in 6, an 8 m
 * leap in 8 — all fit without stretching.
 */
export const TRAVEL_MAX_STEP = MAX_STEP_XZ;
/**
 * The decoy leaves at least at a walk, whatever its owner was doing. A decoy
 * that stood still beside a standing owner is a tell; one that runs on is a
 * body the enemy has to decide about.
 */
export const DECOY_MIN_SPEED = PHYSICS.WALK_SPEED;
// ---------------------------------------------------------------------------
// Match clock
// ---------------------------------------------------------------------------
/** Locked at exactly 4:00 by the PRD. Every zone deadline derives from it. */
export const MATCH_DURATION_MS = 240_000;
export const MATCH_DURATION_TICKS = (MATCH_DURATION_MS * TICK_HZ) / 1000; // 7200
/**
 * Drop phase: spawn, fly-over, invulnerable. Part of the 4:00, not added to it.
 *
 * Eight, not five. Spawns are at a fixed y=6, so the fall itself is over in
 * about a second and the rest of this is standing-around-invulnerable time —
 * which is the point. It is the only stretch of a match where nobody can be
 * hurt, so it is where the pre-fight countdown lives, and three of these
 * seconds are now the 3-2-1 leading into the first legal hit.
 *
 * It costs the fight three seconds, because the drop is inside the 4:00 rather
 * than added to it. That is the trade: a slightly shorter fight that everyone
 * starts together and on purpose.
 */
export const DROP_TICKS = 8 * TICK_HZ;
/** Deaths this close together are simultaneous for tiebreak purposes (250ms). */
export const SIMULTANEOUS_DEATH_TICKS = Math.round(0.25 * TICK_HZ);
/** A kill is credited to whoever landed a hit within this window. */
export const KILL_CREDIT_TICKS = 5 * TICK_HZ;
// ---------------------------------------------------------------------------
// Zone
// ---------------------------------------------------------------------------
/** No zone damage for the first two minutes. */
export const ZONE_DAMAGE_START_TICK = MATCH_DURATION_TICKS - 2 * 60 * TICK_HZ; // 3600
/** The final contraction begins with 30s left. */
export const ZONE_FINAL_START_TICK = MATCH_DURATION_TICKS - 30 * TICK_HZ; // 6300
/**
 * The zone hits radius zero here — six seconds BEFORE the clock expires, not at
 * it. A zone that is still open when the clock runs out means the last two
 * players can kite the timer out, and a wagered match that resolves on a timer
 * pays people for not playing. Closing early forces the fight to happen.
 */
export const ZONE_CLOSED_TICK = MATCH_DURATION_TICKS - 6 * TICK_HZ; // 7020
/** Radius at the end of the first contraction, as a fraction of the start radius. */
export const ZONE_MID_RADIUS_FRACTION = 0.28;
export const ZONE_DPS_START = 6;
export const ZONE_DPS_MID = 10;
export const ZONE_DPS_END = 15;
/** Contractions are announced this far ahead. */
export const ZONE_WARN_TICKS = 5 * TICK_HZ;
/** Zone damage is applied every tick but only reported this often, to spare the wire. */
export const ZONE_HIT_EVENT_INTERVAL_TICKS = Math.round(TICK_HZ / 2);
// ---------------------------------------------------------------------------
// Combat
// ---------------------------------------------------------------------------
/**
 * Kills are the only way to get HP back, and the numbers are chosen so that
 * fighting beats hiding. See GAME-DESIGN "Why aggression has to be paid for".
 */
export const KILL_HEAL = 25;
export const KILL_ULT_CHARGE = 35;
/**
 * Ult charge accrues from damage dealt and damage taken. NEVER from time alive
 * — that would pay players to hide, which is the exact failure mode a
 * winner-take-all royale is prone to.
 */
export const ULT_CHARGE_PER_DAMAGE_DEALT = 0.45;
export const ULT_CHARGE_PER_DAMAGE_TAKEN = 0.3;
export const ULT_FULL = 100;
/**
 * The universal dash. One charge, and the only i-frames in the game.
 *
 * The cooldown and duration are re-exported from `@chaos/protocol` rather than
 * declared here, because the HUD and the shell both quote them and the shell
 * cannot depend on the sim. What stays below is resolution detail nothing
 * outside the simulation needs.
 */
export { DASH_COOLDOWN_MS, DASH_DURATION_MS } from "@chaos/protocol";
export const DASH_SPEED = 42;
/** Gravity is mostly cancelled during a dash so it reads as a horizontal move. */
export const DASH_GRAVITY_MULT = 0.15;
/** The ability id the dash cooldown is filed under. */
export const DASH_ABILITY_ID = "dash";
/** Knockback is stated in metres; this converts it to a launch speed. */
export const KNOCKBACK_SPEED_PER_UNIT = 5.5;
/** A little lift so a knockback reads on screen instead of grinding along the floor. */
export const KNOCKBACK_LIFT_FRACTION = 0.35;
/** Hex's Gravity Well drags targets in over one second. */
export const PULL_DURATION_MS = 1000;
export const PULL_MAX_SPEED = 20;
/** Glyph Trap needs a beat before it can bite, so it cannot be dropped on someone's head. */
export const TRAP_ARM_TICKS = Math.round(0.4 * TICK_HZ);
/**
 * How far above or below the caster a melee swing can still connect. Body
 * height plus body radius: enough to hit anyone you could plausibly reach,
 * not enough to hit a Perched Talon 8m straight up.
 */
export const MELEE_VERTICAL_REACH = PLAYER_HALF_HEIGHT * 2 + PLAYER_RADIUS;
/** Splash radius of one Barrage shell inside the 12m target area. */
export const BARRAGE_SHELL_SPLASH = 3.5;
/**
 * Arc Shot leaves the barrel at 15 degrees. sin/cos precomputed to keep the
 * step trig-free.
 *
 * 15, not 30: at 30 degrees the shell's apex was ~4m up, sailing over every
 * standing body, so on flat ground it could only ever connect at its landing
 * ring (~26-30m) — a 23m dead zone on a 5s-cooldown skill. At 15 degrees the
 * apex stays within reach of a standing capsule, so the shell can strike at any
 * point of its flight; flat-ground carry is ~19m, and firing from high ground
 * (Perch) stretches it toward `maxRange`.
 */
export const PROJECTILE_LAUNCH_SIN = 0.25881904510252074;
export const PROJECTILE_LAUNCH_COS = 0.9659258262890683;
/** Muzzle offset so a shell does not immediately collide with its owner. */
export const PROJECTILE_SPAWN_OFFSET = 1.2;
/** The shell's own physical size in the fly-by contact test. */
export const PROJECTILE_CONTACT_RADIUS = 0.2;
/**
 * How far above the caster's centre a projectile leaves the muzzle. Chest
 * height on a 2-unit body, and inside every standing capsule's vertical span,
 * so a flat bullet fired on level ground meets a body rather than sailing over
 * its head. The client's predicted bullets read the same constant.
 */
export const PROJECTILE_MUZZLE_HEIGHT = 0.4;
/**
 * Largest distance a projectile may advance between two wall tests. A bullet
 * at 46 u/s covers 1.5 units per tick and the thinnest authored wall is 1 unit
 * thick, so a single end-of-tick point test would tunnel straight through it.
 */
export const PROJECTILE_WALL_STEP = 0.5;
/** How far ahead of the caster a placed ground zone lands, by ability id. */
export const GROUND_ZONE_OFFSET = { barrage: 14 };
/** Snapshot ids for decoys. The room must reject player ids carrying this prefix. */
export const DECOY_ID_PREFIX = "decoy#";
//# sourceMappingURL=constants.js.map